import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { activityApi } from '../api/activity.api';

// Map known route paths to friendly tab names for the activity log.
const PAGE_TITLES: { match: RegExp; title: string }[] = [
  { match: /^\/dashboard/, title: 'Dashboard' },
  { match: /^\/calendar/, title: 'Calendar' },
  { match: /^\/squads\/[^/]+/, title: 'Squad Details' },
  { match: /^\/squads/, title: 'GKM Squads' },
  { match: /^\/sessions/, title: 'My Squads / Sessions' },
  { match: /^\/session-plans/, title: 'Session Plans' },
  { match: /^\/competitions/, title: 'Competitions' },
  { match: /^\/knowledge/, title: 'Knowledge Center' },
  { match: /^\/snc/, title: 'S&C' },
  { match: /^\/news\/create/, title: 'Create News' },
  { match: /^\/news\/[^/]+\/edit/, title: 'Edit News' },
  { match: /^\/news\/[^/]+/, title: 'News Article' },
  { match: /^\/news/, title: 'News' },
  { match: /^\/notifications\/center/, title: 'Notification Center' },
  { match: /^\/users/, title: 'User Management' },
  { match: /^\/profile/, title: 'Profile' },
  { match: /^\/admin/, title: 'Administration' },
];

// Detail routes: /<resource>/<id>. For these we wait for the page to supply
// the object's readable name, then log a single enriched entry.
// Only routes that have a dedicated detail page are listed here.
const DETAIL_ROUTE = /^\/(squads|news)\/([^/]+)(?:\/|$)/;

function titleForPath(path: string): string {
  const found = PAGE_TITLES.find((p) => p.match.test(path));
  return found ? found.title : path;
}

function detailMatch(path: string): { id: string } | null {
  const m = path.match(DETAIL_ROUTE);
  if (!m) return null;
  const id = m[2];
  if (id === 'create') return null;
  return { id };
}

// Dedupe guard: remembers the last logged "path|name" and when, so repeated
// calls (e.g. React 18 StrictMode double-invoking effects in dev, or a page
// re-fetching) don't create duplicate entries.
let lastReported: { key: string; at: number } | null = null;
const REPORT_DEDUPE_MS = 5000;

/**
 * Called by a detail page once it has loaded its object, to log a single
 * enriched activity entry for the current route (page title + object name + id).
 */
export function reportViewedObject(path: string, name: string): void {
  if (!name) return;

  const key = `${path}|${name}`;
  const now = Date.now();
  if (lastReported && lastReported.key === key && now - lastReported.at < REPORT_DEDUPE_MS) {
    return; // Skip duplicate within the dedupe window.
  }
  lastReported = { key, at: now };

  const title = titleForPath(path);
  const match = detailMatch(path);
  activityApi.logPageView(path, title, match?.id, name).catch(() => {});
}

/**
 * Logs a PAGE_VIEW activity whenever the authenticated user navigates to a
 * new route. Mount once inside the authenticated layout.
 *
 * - For list/section routes: logs immediately with the page title.
 * - For detail routes (/<resource>/<id>): does NOT log here — the detail page
 *   calls reportViewedObject() once it has the object name, producing a single
 *   enriched entry (name + id) instead of a duplicate id-only one.
 */
export const usePageViewLogger = () => {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const path = location.pathname;

    if (lastPath.current === path) {
      return;
    }
    lastPath.current = path;

    // Detail routes are logged by the page (with name) — skip here to avoid
    // a duplicate, id-only entry.
    if (detailMatch(path)) {
      return;
    }

    activityApi.logPageView(path, titleForPath(path)).catch(() => {
      // Ignore — page-view logging must never disrupt the UI.
    });
  }, [location.pathname]);
};
